import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Send, MapPin, Mail, Clock } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};
    if (!formData.name || formData.name.length < 2) newErrors.name = 'Name must be at least 2 characters';
    if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Valid email is required';
    if (!formData.message || formData.message.length < 10) newErrors.message = 'Message must be at least 10 characters';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact`, formData);
      if (response.data.success) {
        toast.success('Message sent successfully!', {
          description: "We'll get back to you shortly.",
        });
        setFormData({ name: '', email: '', company: '', message: '' });
        setErrors({});
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to send message. Please try again.';
      toast.error('Something went wrong', { description: msg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-16 sm:py-20 bg-white" data-testid="contact-section">
      <div className="container-main">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left: Form */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <p className="font-mono-accent text-xs font-medium uppercase tracking-[0.18em] text-[#7C3AED] mb-3">
              Contact us
            </p>
            <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[#0B0B10] mb-4">
              Get in touch
            </h2>
            <p className="text-base text-[#64647A] mb-8">
              Interested in TexSolution? Tell us about your needs and we'll show you how we can help.
            </p>

            <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-5">
              <div>
                <Label htmlFor="name" className="text-sm font-medium text-[#0B0B10] mb-1.5 block">Name</Label>
                <Input
                  id="name"
                  data-testid="contact-form-name-input"
                  placeholder="Your name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="rounded-xl h-11 border-[#E7E7EF] focus:border-[#7C3AED] focus:ring-[#7C3AED]"
                  aria-describedby={errors.name ? 'name-error' : undefined}
                />
                {errors.name && <p id="name-error" className="text-xs text-[#DC2626] mt-1">{errors.name}</p>}
              </div>

              <div>
                <Label htmlFor="email" className="text-sm font-medium text-[#0B0B10] mb-1.5 block">Email</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="contact-form-email-input"
                  placeholder="you@company.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="rounded-xl h-11 border-[#E7E7EF] focus:border-[#7C3AED] focus:ring-[#7C3AED]"
                  aria-describedby={errors.email ? 'email-error' : undefined}
                />
                {errors.email && <p id="email-error" className="text-xs text-[#DC2626] mt-1">{errors.email}</p>}
              </div>

              <div>
                <Label htmlFor="company" className="text-sm font-medium text-[#0B0B10] mb-1.5 block">
                  Company <span className="text-[#A0A0B0]">(optional)</span>
                </Label>
                <Input
                  id="company"
                  data-testid="contact-form-company-input"
                  placeholder="Your company"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  className="rounded-xl h-11 border-[#E7E7EF] focus:border-[#7C3AED] focus:ring-[#7C3AED]"
                />
              </div>

              <div>
                <Label htmlFor="message" className="text-sm font-medium text-[#0B0B10] mb-1.5 block">Message</Label>
                <Textarea
                  id="message"
                  data-testid="contact-form-message-textarea"
                  placeholder="Tell us about your needs..."
                  rows={4}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="rounded-xl border-[#E7E7EF] focus:border-[#7C3AED] focus:ring-[#7C3AED] resize-none"
                  aria-describedby={errors.message ? 'message-error' : undefined}
                />
                {errors.message && <p id="message-error" className="text-xs text-[#DC2626] mt-1">{errors.message}</p>}
              </div>

              <Button
                type="submit"
                data-testid="contact-form-submit-button"
                disabled={loading}
                className="bg-[#7C3AED] text-white hover:bg-[#6D28D9] rounded-xl h-11 px-6 text-sm shadow-sm hover:shadow-md btn-press w-full sm:w-auto"
              >
                {loading ? (
                  <span className="flex items-center">
                    <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Sending...
                  </span>
                ) : (
                  <span className="flex items-center">
                    Send message
                    <Send className="ml-2 h-4 w-4" />
                  </span>
                )}
              </Button>
            </form>
          </motion.div>

          {/* Right: Company info */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="flex flex-col gap-6"
          >
            <div className="bg-[#F5F3FF] rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-[#0B0B10] mb-4">Our offices</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-[#7C3AED] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-[#0B0B10]">London, United Kingdom</p>
                    <p className="text-xs text-[#64647A]">Global headquarters</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-[#7C3AED] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-[#0B0B10]">Singapore</p>
                    <p className="text-xs text-[#64647A]">Asia-Pacific operations</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-[#7C3AED] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-[#0B0B10]">Dubai, UAE</p>
                    <p className="text-xs text-[#64647A]">Middle East & Africa operations</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white border border-[#E7E7EF] rounded-2xl p-6">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Mail className="h-5 w-5 text-[#7C3AED] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-[#0B0B10]">info@texsolutions.pro</p>
                    <p className="text-xs text-[#64647A]">General inquiries & partnerships</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Clock className="h-5 w-5 text-[#7C3AED] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-[#0B0B10]">24/7 support</p>
                    <p className="text-xs text-[#64647A]">We respond within 4 hours</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
